import { WhatsAppPrimaryContent } from 'modules/campaigns/editor/types';
import {
  MetaApprovalStatusDefinition,
  MetaQualityRating,
  WhatsAppContentDefinition,
} from 'modules/graphql';
import { WHATSAPP_SENDER_RESTRICTED_STATUSES } from 'modules/templateEditor/common/constants';
import { WHATSAPP_LOCALE_STATUS_AND_QUALITY_WARNING_MESSAGE } from '../constants';
import { WhatsAppLocaleContent } from 'modules/templateEditor/common';
import { getWhatsAppSenderRestrictedWarningMessage } from './getWhatsAppSenderRestrictedWarningMessage';

const pausedAndDisabledStatuses = [
  MetaApprovalStatusDefinition.FirstPaused,
  MetaApprovalStatusDefinition.SecondPaused,
  MetaApprovalStatusDefinition.Disabled,
];

/**
 * Get warning messages related to a WhatsApp template
 * @param content Can be one of:
 * 1. An object with primaryContent and localeContents
 * 2. An array of WhatsAppLocaleContent
 * 3. undefined
 * @returns an array of warning messages with the following order:
 * 1- senders restricted,
 * 2- low quality locales,
 * 3- paused or disabled locales
 */

export const getTemplateWhatsAppWarnings = (
  content:
    | {
        primaryContent: WhatsAppPrimaryContent | undefined;
        localeContents: WhatsAppContentDefinition[] | undefined;
      }
    | WhatsAppLocaleContent[]
    | undefined,
) => {
  if (!content) return [];

  const allContents = normalizeContent(content);
  if (!allContents.length) return [];

  let warnings: { title: string; description: string }[] = [];

  const limitedSenders = allContents.filter(
    ({ whatsAppSender }) =>
      !!whatsAppSender?.status &&
      WHATSAPP_SENDER_RESTRICTED_STATUSES.includes(whatsAppSender.status) &&
      !!whatsAppSender?.sendLimit,
  );

  const lowQualityLocales = allContents.filter(
    ({ qualityRating }) => qualityRating === MetaQualityRating.Low,
  );

  const pausedAndDisabledLocales = allContents.filter(
    ({ metaApprovalStatus }) =>
      !!metaApprovalStatus && pausedAndDisabledStatuses.includes(metaApprovalStatus),
  );

  if (limitedSenders.length) {
    warnings.push(
      getWhatsAppSenderRestrictedWarningMessage(
        limitedSenders[0].whatsAppSender?.sendLimit,
        limitedSenders.length > 1,
      ),
    );
  }

  if (lowQualityLocales.length) {
    const message = WHATSAPP_LOCALE_STATUS_AND_QUALITY_WARNING_MESSAGE[MetaQualityRating.Low];
    if (message) warnings.push(message);
  }

  if (pausedAndDisabledLocales.length) {
    pausedAndDisabledStatuses.forEach((status) => {
      const statusLocales = pausedAndDisabledLocales.filter(
        ({ metaApprovalStatus }) => metaApprovalStatus === status,
      );

      if (statusLocales.length) {
        const message = WHATSAPP_LOCALE_STATUS_AND_QUALITY_WARNING_MESSAGE[status];
        if (message) warnings.push(message);
      }
    });
  }

  return warnings;
};

/**
 * Helper function to normalize different content formats into a single array
 * since all of them include qualityRating, metaApprovalStatus, and whatsAppSender
 */
function normalizeContent(
  content:
    | {
        primaryContent: WhatsAppPrimaryContent | undefined;
        localeContents: WhatsAppContentDefinition[] | undefined;
      }
    | WhatsAppLocaleContent[],
): (WhatsAppPrimaryContent | WhatsAppContentDefinition | WhatsAppLocaleContent)[] {
  if (Array.isArray(content)) {
    return content;
  }

  return [
    ...(content.primaryContent ? [content.primaryContent] : []),
    ...(content.localeContents || []),
  ];
}
